import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { StateService, UIRouterGlobals } from '@uirouter/angular';
import { Subscription } from 'rxjs';

import { CimaCore, Cloudinary, Formatter, Util, XmStore, XmStoreUtil } from 'core/services';
import { SwitchUtil } from '../../../shared/services/switch-util';

import { CmsLineDataPlanCollection, CmsPlanSwitch } from 'store/cms/models';
import { AccountSummary, LineSummary, PlanSummary } from 'store/account-summary/models';
import { LineDataPlan, PlanEligibility } from 'store/line/models';
import { User } from 'store/user/models';
import { DataPlanType, UnlPlanSubgroupName } from 'my-account/constants';
import { DiscountPromoCollection, DiscountPromotion } from 'store/promotions/discount-promotion/models';
import { StoreAction } from 'store/actions';
import { IXMOptions } from 'core/interfaces';
import { CONFIG_TOKEN } from 'core/constants';
import { targetPlanSwitchDetails } from 'store/plan-eligibility/models/target-plan-switch';

type UnlimitedTierPlan = CmsUnlimitedTierPlan & LineDataPlan;

@Component({
    selector: 'plan-switch-unl-tiers-review',
    styleUrls: ['./unl-tiers-review.scss'],
    templateUrl: 'unl-tiers-review.html',
    standalone: false
})
export class PlanSwitchUnlimitedTiersReviewComponent implements OnInit, OnDestroy {
    public pageData: CmsPlanSwitchUnlimitedTiersReview;
    public user: User;
    public dataPlanCollection: CmsLineDataPlanCollection;
    public selectedPlanData: CmsDataPlanDetails;
    public introUpgrade: CmsPlanSwitchUnlimitedIntroUpgrade;
    public plusUpgradeDowngrade: CmsPlanSwitchUnlimitedPlusUpgradeDowngrade;
    public premiumDowngrade: CmsPlanSwitchUnlimitedPremiumDowngrade;
    public lineSummary: LineSummary;
    public planEligibility: PlanEligibility;
    public eligiblePlanDetails: LineDataPlan;
    public eligiblePlans: LineDataPlan[];
    public isUnlimited: boolean;
    public tierData: UnlimitedTierPlan[];
    public isButtonEnabled: boolean = false;
    public selectedPlanCode: string;
    public buttonText: string;
    public inEligibleInUsePlan: UnlimitedTierPlan;
    public isBusinessUser: boolean;
    public inEligibleInUsePlanCode: string;
    public highlightedPlan: CmsDataPlanDetails;
    public promotionData: string[] = [];
    public discountPromoResp: DiscountPromoCollection;
    public promoIcon: MediaImageOptions;
    public promotionHeader: string;
    public multiplePromotions: CmsPlanSwitchMultiplePromotions;
    public seg5BtgToUnlSwitchBanner: CmsSeg5BtgToUnlSwitchBanner;
    public circleDeviceIcon: MediaImageOptions;
    public priceSymbol: string = '';
    public enablePlanSevenPlanSwitchCBM: boolean;
    public showPlanUpgradeBanner: boolean;
    public plans: PlanSummary[];
    public summary: AccountSummary;
    public legacyPromoDetails: targetPlanSwitchDetails;
    public isIcopPlanSwitch: boolean;
    public isBillPreviewEnabled: boolean;
    public reviewDetails: string;
    public showSegmentUpgradeBanner: boolean;
    public segmentSwitchBanner: NotificationBanner;
    public segmentSwitchIcon: MediaImageOptions;

    private subscriptions: Subscription[] = [];
    private xmStore: XmStore;
    private state: StateService;
    private globalRouterState: UIRouterGlobals;
    private cimaCore: CimaCore;
    private isCbmUserSegmentFiveUnlimitedIntro: boolean;
    private isCbmUserSegmentFiveUnlimitedPremium: boolean;
    private isCbmUserSegmentFiveTabletUnlimitedIntro: boolean;
    private isCbmUserSegmentFiveTabletUnlimitedPremium: boolean;
    private isSegmentFiveUser: boolean;
    private config: IXMOptions;

    constructor(xmStore: XmStore, state: StateService, switchUtil: SwitchUtil, cimaCore: CimaCore, globalRouterState: UIRouterGlobals, @Inject(CONFIG_TOKEN) config: IXMOptions) {
        Object.assign(this, { xmStore, state, switchUtil, cimaCore, globalRouterState, config });
    }

    public ngOnInit(): void {
        this.isBusinessUser = this.cimaCore.isBusinessUser;
        this.enablePlanSevenPlanSwitchCBM = this.isBusinessUser;
        this.isIcopPlanSwitch = this.config.ENABLE_ICOP_P1_PLANSWITCH;
        this.isBillPreviewEnabled = (this.config.ENABLE_BILL_PREVIEW && !this.isBusinessUser) || (this.config.ENABLE_CBM_BILL_PREVIEW && this.isBusinessUser);


        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<AccountSummary>(AccountSummary), (summary: AccountSummary) => {
            this.summary = summary;
            this.lineSummary = summary.getLineByKey(this.globalRouterState.params.line_id);
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User), (user: User) => {
            this.user = user;
            this.isCbmUserSegmentFiveUnlimitedIntro = this.isBusinessUser && this.user.account.isSegmentFive && this.lineSummary.isUnlBasic && !this.lineSummary.isTablet;
            this.isCbmUserSegmentFiveUnlimitedPremium = this.isBusinessUser && this.user.account.isSegmentFive && this.lineSummary.isUnlPremium && !this.lineSummary.isTablet;
            this.isCbmUserSegmentFiveTabletUnlimitedIntro = this.isBusinessUser && this.user.account.isSegmentFive && this.lineSummary.isUnlBasic && this.lineSummary.isTablet;
            this.isCbmUserSegmentFiveTabletUnlimitedPremium = this.isBusinessUser && this.user.account.isSegmentFive && this.lineSummary.isUnlPremium && this.lineSummary.isTablet;
            this.isSegmentFiveUser = this.user.account.isSegmentFive;
            this.showPlanUpgradeBanner = (!this.isBusinessUser || this.enablePlanSevenPlanSwitchCBM) && this.isSegmentFiveUser && this.summary.getBtgLineCountAndLineId().lineCount === 1 
                && (this.lineSummary.isBtgLine || this.lineSummary.id.key === this.summary.getBtgLineCountAndLineId().lineId);
            this.showSegmentUpgradeBanner = this.user.account.canMoveLevel || (!this.isBusinessUser && this.user.account.isSegmentFive
                && !this.user.account.isNYCustomer);
        }));

        if (this.showSegmentUpgradeBanner) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<targetPlanSwitchDetails>(targetPlanSwitchDetails), (legacyPromoDetails: targetPlanSwitchDetails) => {
                this.legacyPromoDetails = legacyPromoDetails;
                this.segmentSwitchBanner = this.legacyPromoDetails.selectionPage?.banners.length ? this.legacyPromoDetails.selectionPage.banners[0] : undefined;
                this.segmentSwitchIcon = {
                    mobile: {
                        base: this.segmentSwitchBanner?.image
                    }
                };
            }));
        }

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsPlanSwitch>(CmsPlanSwitch), (result: CmsPlanSwitch) => {
            this.reviewDetails = result.reviewDetails;
            this.pageData = this.getPageData(result);
            this.multiplePromotions = result.planSwitchMultiplePromotions;
            this.seg5BtgToUnlSwitchBanner = result.seg5BtgToUnlSwitchBanner;
            this.promoIcon = this.generatePromotionIcon(result.dollarIcon);
            this.buttonText = this.pageData.switchCta;
            this.circleDeviceIcon = {
                mobile: {
                    base: this.seg5BtgToUnlSwitchBanner.mobileImage
                },
                smallDesktop: {
                    base: this.seg5BtgToUnlSwitchBanner.desktopImage
                }
            };
            
            if (this.user.account.isSegmentFive) {
                if (this.isBusinessUser) {
                    this.introUpgrade = result.planSwitchUnlimitedIntroUpgradeForSeg5;
                    this.premiumDowngrade = result.planSwitchUnlimitedCbmPremiumDowngrade;
                } else {
                    this.introUpgrade = result.planSwitchUnlimitedIntroUpgradeForSeg5;
                    this.plusUpgradeDowngrade = result.planSwitchUnlimitedPlusUpgradeDowngradeForSeg5;
                }
            } else {
                this.introUpgrade = result.planSwitchUnlimitedIntroUpgradeForOtherSeg;
                this.plusUpgradeDowngrade = result.planSwitchUnlimitedPlusUpgradeDowngradeForOtherSeg;
                this.premiumDowngrade = result.planSwitchUnlimitedPremiumDowngradeForOtherSeg;
            }
            
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLineDataPlanCollection>(CmsLineDataPlanCollection), (result: CmsLineDataPlanCollection) => {
            this.dataPlanCollection = result;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<PlanEligibility>(PlanEligibility, {
            filters: { lineKey: this.globalRouterState.params.line_id }
        }), (planEligibility: PlanEligibility) => {
            this.planEligibility = planEligibility;
            this.eligiblePlans = this.planEligibility.plans;

            this.tierData = this.buildUnlTierObject(this.pageData.tiers, this.eligiblePlans);
        }));

        if (!this.isIcopPlanSwitch) {
            XmStoreUtil.defaultCatch(this.xmStore.findPromise<DiscountPromoCollection>(StoreAction.GET_DISCOUNT_PROMOTION, DiscountPromoCollection))
                .then((response: DiscountPromoCollection) => {
                    this.discountPromoResp = response;
                    if (this.user.account.isSegmentFive) {
                        this.setPromotionDataForSeg5();
                    } else {
                        this.setPromotionData();
                    }
                });
        }
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    public showInUseBadge(tier: UnlimitedTierPlan): boolean {

        if (tier.eligibilityStatus === 'INELIGIBLE_IN_USE') {
            this.inEligibleInUsePlan = tier;
            if (this.lineSummary.isTablet) {
                this.inEligibleInUsePlan.name = (this.inEligibleInUsePlan.name === UnlPlanSubgroupName.TABLET_INTRO) ? UnlPlanSubgroupName.TABLET_INTRO
                    : (this.inEligibleInUsePlan.name === UnlPlanSubgroupName.TABLET_PREMIUM) ? UnlPlanSubgroupName.TABLET_PREMIUM
                        : this.inEligibleInUsePlan.name;
            } else {
                this.inEligibleInUsePlan.name = (this.inEligibleInUsePlan.name === UnlPlanSubgroupName.INTRO) ? UnlPlanSubgroupName.INTRO
                    : (this.inEligibleInUsePlan.name === UnlPlanSubgroupName.PREMIUM) ? UnlPlanSubgroupName.PREMIUM
                        : this.inEligibleInUsePlan.name;
            }
            this.inEligibleInUsePlanCode = this.inEligibleInUsePlan.planCode;

            if (!(this.isBusinessUser && (this.isCbmUserSegmentFiveUnlimitedIntro || this.isCbmUserSegmentFiveTabletUnlimitedIntro || this.isCbmUserSegmentFiveTabletUnlimitedPremium))) {
                this.pageData.description = this.inEligibleInUsePlan.name === UnlPlanSubgroupName.PREMIUM ? this.premiumDowngrade.description
                    : this.inEligibleInUsePlan.name === UnlPlanSubgroupName.PLUS ? this.plusUpgradeDowngrade.description
                        : this.introUpgrade.description;
            }

            this.selectedPlanData = this.dataPlanCollection.plans.find((cmsPlan: CmsDataPlanDetails) => cmsPlan.planCode.includes(this.inEligibleInUsePlanCode));
        }

        return tier.isIneligibleInUse;

    }

    public showScheduledBadge(tier: UnlimitedTierPlan): boolean {
        return tier.isIneligibleScheduled;
    }

    public onEmitPlanCode(planCode: string): void {
        this.isButtonEnabled = true;

        if (this.inEligibleInUsePlanCode?.includes(planCode)) {
            this.isButtonEnabled = false;
        }

        const selectedPlan: LineDataPlan = this.eligiblePlans.find((plan: LineDataPlan) => plan.planCode.startsWith(planCode));
        this.selectedPlanCode = selectedPlan.planCode;

        this.highlightedPlan = this.dataPlanCollection.plans.find((cmsPlan: CmsDataPlanDetails) => cmsPlan.planCode.find((plan: string) => plan.startsWith(planCode)));

        if (this.isBillPreviewEnabled) {
            this.buttonText = this.reviewDetails;
        } else if (this.inEligibleInUsePlan) {
            //Premium logic:
            if (this.inEligibleInUsePlan.name === UnlPlanSubgroupName.PREMIUM) {
                this.buttonText = this.highlightedPlan.planName === UnlPlanSubgroupName.PREMIUM ? this.premiumDowngrade.continuePlanCta
                    : (this.highlightedPlan.planName === UnlPlanSubgroupName.INTRO || this.highlightedPlan.planName === UnlPlanSubgroupName.PLUS) ? this.premiumDowngrade.scheduledSwitchCta
                        : this.premiumDowngrade.switchCta;
            }
            //Plus Logic
            if (this.inEligibleInUsePlan.name === UnlPlanSubgroupName.PLUS) {
                this.buttonText = this.highlightedPlan.planName === UnlPlanSubgroupName.PLUS ? this.plusUpgradeDowngrade.continuePlanCta
                    : (this.highlightedPlan.planName === UnlPlanSubgroupName.INTRO || this.highlightedPlan.planName === UnlPlanSubgroupName.PREMIUM) ? this.plusUpgradeDowngrade.scheduledSwitchCta
                        : this.plusUpgradeDowngrade.switchCta;
            }
            //Intro Logic
            if (this.inEligibleInUsePlan.name === UnlPlanSubgroupName.INTRO) {
                this.buttonText = this.highlightedPlan.planName === UnlPlanSubgroupName.INTRO ? this.introUpgrade.continuePlanCta
                    : (this.highlightedPlan.planName === UnlPlanSubgroupName.PLUS || this.highlightedPlan.planName === UnlPlanSubgroupName.PREMIUM) ? this.introUpgrade.scheduledSwitchCta
                        : this.introUpgrade.switchCta;
            }
            //Tablet Premium Logic
            if (this.inEligibleInUsePlan.name === UnlPlanSubgroupName.TABLET_PREMIUM) {
                this.buttonText = this.highlightedPlan.tabletPlanName === UnlPlanSubgroupName.TABLET_PREMIUM ? this.premiumDowngrade.continuePlanCta
                    : this.highlightedPlan.tabletPlanName === UnlPlanSubgroupName.TABLET_INTRO ? this.premiumDowngrade.scheduledSwitchCta
                        : this.premiumDowngrade.switchCta;
            }
            //Tablet Intro Logic
            if (this.inEligibleInUsePlan.name === UnlPlanSubgroupName.TABLET_INTRO) {
                this.buttonText = this.highlightedPlan.tabletPlanName === UnlPlanSubgroupName.TABLET_INTRO ? this.introUpgrade.continuePlanCta
                    : this.introUpgrade.switchCta;
            }
        }
    }

    public onCancel(): void {
        this.state.go('account.activity.details', { line_id: this.globalRouterState.params.line_id });
    }

    public navToSwitchInProgress(): void {
        if (this.isBillPreviewEnabled) {
            this.state.go('account.activity.details.plan-switch.review-order', { line_id: this.globalRouterState.params.line_id, plan_code: this.selectedPlanCode, backButtonPath: 'account.activity.details.plan-switch.unl-tiers-review' });
        } else {
            this.state.go('account.activity.details.plan-switch.in-progress', { line_id: this.globalRouterState.params.line_id, plan_code: this.selectedPlanCode });
        }
    }

    public showPlanNameHeader(): string {
        return this.selectedPlanData ? this.selectedPlanData.planType : DataPlanType.UNLIMITED;
    }

    public generatePromotionIcon(image: CmsMediaImage): MediaImageOptions {
        return {
            mobile: Cloudinary.generateMediaFromCms(image)
        };
    }

    public showPrice(tier: UnlimitedTierPlan): number {
        if (this.user.account.isSegmentFive) {
            const inUsePlan: boolean = this.tierData.some((plan: UnlimitedTierPlan) => plan.isIneligibleInUse);
            if (inUsePlan) {
                if (tier.isIneligibleInUse) {
                    return tier.postSwitch.lineCost;
                } else {
                    if (this.isCbmUserSegmentFiveUnlimitedIntro || this.isCbmUserSegmentFiveTabletUnlimitedIntro) {
                        return tier.postSwitch.lineCost;
                    } else if (this.isCbmUserSegmentFiveUnlimitedPremium || this.isCbmUserSegmentFiveTabletUnlimitedPremium) {
                        return tier.postSwitch.lineCost;
                    } else {
                        return tier.name === UnlPlanSubgroupName.PLUS ? this.introUpgrade.price : this.plusUpgradeDowngrade.price;
                    }
                }
            }

            return tier.postSwitch.lineCost;
        } else {
            return tier.postSwitch.lineCost;
        }
    }

    public showPriceSymbol(tier: UnlimitedTierPlan): string {
        if (this.user.account.isSegmentFive) {
            const inUsePlan: boolean = this.tierData.some((plan: UnlimitedTierPlan) => plan.isIneligibleInUse);
            if (inUsePlan) {
                if (tier.name === UnlPlanSubgroupName.PLUS) {
                    this.priceSymbol = this.introUpgrade.priceSymbol;
                } else if (this.isCbmUserSegmentFiveUnlimitedIntro || this.isCbmUserSegmentFiveTabletUnlimitedIntro) {
                    this.priceSymbol = '';
                } else if (this.isCbmUserSegmentFiveUnlimitedPremium || this.isCbmUserSegmentFiveTabletUnlimitedPremium) {
                    this.priceSymbol = '';
                } else {
                    this.priceSymbol = this.plusUpgradeDowngrade.priceSymbol;
                }
            }
        }

        return this.priceSymbol;
    }

    private buildUnlTierObject(cmsTiers: CmsUnlimitedTierPlan[], plans: LineDataPlan[]): UnlimitedTierPlan[] {
        const tierData: UnlimitedTierPlan[] = [];
        cmsTiers.map((tier: CmsUnlimitedTierPlan) => {
            if (plans.some((plan: LineDataPlan) => plan.planCode.startsWith(tier.radioOption.value))) {
                const currentPlan: LineDataPlan = plans.find((plan: LineDataPlan) => plan.planCode.startsWith(tier.radioOption.value));
                // eslint-disable-next-line prefer-object-spread
                tierData.push(Object.assign({}, tier, currentPlan));
            }
        });

        return tierData;
    }

    private setPromotionData(): void {

        if (this.multiplePromotions && this.discountPromoResp) {
            this.promotionHeader = this.discountPromoResp.isTradeInDDAPromoEligible && (this.inEligibleInUsePlan.name === UnlPlanSubgroupName.PLUS || this.inEligibleInUsePlan.name === UnlPlanSubgroupName.PREMIUM || 
                    this.inEligibleInUsePlan.name === UnlPlanSubgroupName.TABLET_PREMIUM) ? this.multiplePromotions.unlTiersReviewContent.generalDowngrade : this.multiplePromotions.unlTiersReviewContent.general; 
            this.promotionData = this.getPromoDescription(this.promotionData);
        }
    }

    private setPromotionDataForSeg5(): void {

        if (this.multiplePromotions && this.discountPromoResp) {
            this.promotionHeader = this.discountPromoResp.isTradeInDDAPromoEligible && (this.inEligibleInUsePlan.name === UnlPlanSubgroupName.PLUS || this.inEligibleInUsePlan.name === UnlPlanSubgroupName.PREMIUM || 
                this.inEligibleInUsePlan.name === UnlPlanSubgroupName.TABLET_PREMIUM) ? this.multiplePromotions.unlTiersReviewContentSeg5.generalDowngrade : this.multiplePromotions.unlTiersReviewContentSeg5.general;
            this.promotionData = this.getPromoDescription(this.promotionData);
        }
    }

    private getPromoDescription(promotionData: string[]): string[] {
        if (this.discountPromoResp.accountPromoEligible?.length > 0) {
            if (this.user.account.isSegmentFive) {
                this.discountPromoResp.accountPromoEligible.map((promo: DiscountPromotion) => {
                    promotionData.push(this.getPromoDateAmount(this.multiplePromotions.unlTiersReviewContentSeg5.UNL, promo));
                });
            } else {
                this.discountPromoResp.accountPromoEligible.map((promo: DiscountPromotion) => {
                    promotionData.push(this.getPromoDateAmount(this.multiplePromotions.unlTiersReviewContent.UNL, promo));
                });
            }
        }

        if (this.discountPromoResp.linePromoUNLEligible?.length > 0) {
            if (this.user.account.isSegmentFive) {
                this.discountPromoResp.linePromoUNLEligible.map((promo: DiscountPromotion) => {
                    promotionData.push(this.getPromoDateAmount(this.multiplePromotions.unlTiersReviewContentSeg5.UNL, promo));
                });
            } else {
                this.discountPromoResp.linePromoUNLEligible.map((promo: DiscountPromotion) => {
                    promotionData.push(this.getPromoDateAmount(this.multiplePromotions.unlTiersReviewContent.UNL, promo));
                });
            }
        }

        if (this.discountPromoResp.linePromoBYOEligible?.length > 0) {
            if (this.user.account.isSegmentFive) {
                this.discountPromoResp.linePromoBYOEligible.map((promo: DiscountPromotion) => {
                    promotionData.push(this.getPromoDateAmount(this.multiplePromotions.unlTiersReviewContentSeg5.BYO, promo));
                });
            } else {
                this.discountPromoResp.linePromoBYOEligible.map((promo: DiscountPromotion) => {
                    promotionData.push(this.getPromoDateAmount(this.multiplePromotions.unlTiersReviewContent.BYO, promo));
                });
            }
        }

        if (this.discountPromoResp.isTradeInDDAPromoEligible) {
            const tradeInPromo: DiscountPromotion = this.discountPromoResp?.mvnoPromotions.find((promo: DiscountPromotion) => 
                promo.isTradeInDDAPromoEligible && (promo.lineId === this.lineSummary.id.key || promo.lineId === this.lineSummary.id.value)
            );

            if (tradeInPromo) {
                if (this.user.account.isSegmentFive) {
                    promotionData.push(this.multiplePromotions.unlTiersReviewContentSeg5.DDA.replace('[AMOUNT]', tradeInPromo.billerInfo.promotionAmount.toString()));
                } else {
                    promotionData.push(this.multiplePromotions.unlTiersReviewContent.DDA.replace('[AMOUNT]', tradeInPromo.billerInfo.promotionAmount.toString()));
                }
            }
        }

        if (this.discountPromoResp.isExtendedPromoDollarEligible) {
            const extendedDDAPromo: DiscountPromotion = this.discountPromoResp?.mvnoPromotions.find((promo: DiscountPromotion) => 
                promo.isExtendedPromoDollarEligible && (promo.lineId === this.lineSummary.id.key || promo.lineId === this.lineSummary.id.value)
            );

            if (extendedDDAPromo) {
                if (this.user.account.isSegmentFive) {
                    promotionData.push(this.multiplePromotions.unlTiersReviewContentSeg5.EPD.replace('[PROMO_AMOUNT]', extendedDDAPromo.billerInfo.promotionAmount.toString()));
                } else {
                    promotionData.push(this.multiplePromotions.unlTiersReviewContent.EPD.replace('[PROMO_AMOUNT]', extendedDDAPromo.billerInfo.promotionAmount.toString()));
                }
            }
        }

        return promotionData;
    }

    private getPromoDateAmount(description: string, promoResponse: DiscountPromotion): string {

        return description.replace('[AMOUNT]', promoResponse.billerInfo.monthlyPromotionValue).replace('[DATE]', Formatter.formatMonthDateYear(promoResponse.billerInfo.promotionExpiration));
    }

    private getPageData(cmsData: CmsPlanSwitch): CmsPlanSwitchUnlimitedTiersReview {
        if (this.isBusinessUser) {
            if (this.user.account.isSegmentFive) {
                if (this.isCbmUserSegmentFiveUnlimitedIntro) {
                    return cmsData.cbmPlanSwitchUnlimitedTiersReviewForSeg5IntroToPremium;
                } else if (this.isCbmUserSegmentFiveUnlimitedPremium) {
                    return cmsData.cbmPlanSwitchUnlimitedTiersReviewForSeg5PremiumToIntro;
                } else if (this.isCbmUserSegmentFiveTabletUnlimitedIntro) {
                    return cmsData.cbmPlanSwitchUnlimitedTiersReviewSeg5TabletIntroToPremium;
                } else if (this.isCbmUserSegmentFiveTabletUnlimitedPremium) {
                    return cmsData.cbmPlanSwitchUnlimitedTiersReviewSeg5TabletPremiumToIntro;
                } else {
                    return cmsData.cbmPlanSwitchUnlimitedTiersReviewForSeg5;
                }
            } else {
                return cmsData.cbmPlanSwitchUnlimitedTiersReview;
            }
        } else {
            if (this.lineSummary.isTablet) {
                return cmsData.planSwitchUnlimitedTabletTiersReview;
            } else {
                if (this.user.account.isSegmentFive) {
                    return cmsData.planSwitchUnlimitedTiersReviewForSeg5;
                } else if (!this.user.account.isSegmentFive) {
                    return cmsData.planSwitchUnlimitedTiersReviewForOtherSegs;
                } else {
                    return cmsData.planSwitchUnlimitedTiersReview;
                }
            }
        }
    }
}
